/**
 * Основной макет приложения.
 * Содержит навигационную панель и область контента.
 * Навигация адаптируется в зависимости от роли пользователя.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Навигационная панель */}
      <nav style={styles.nav}>
        <div style={styles.navContent}>
          <Link to="/" style={styles.logo}>
            MS Health Monitor
          </Link>

          {user && (
            <div style={styles.navLinks}>
              {/* Навигация для пациента */}
              {user.role === 'patient' && (
                <>
                  <Link to="/patient/dashboard" style={styles.link}>Дашборд</Link>
                  <Link to="/patient/diary" style={styles.link}>Дневник</Link>
                  <Link to="/patient/tests" style={styles.link}>Тесты</Link>
                  <Link to="/patient/relapses" style={styles.link}>Обострения</Link>
                  <Link to="/patient/profile" style={styles.link}>Профиль</Link>
                  <Link to="/patient/notifications" style={styles.link}>Уведомления</Link>
                  <Link to="/messages" style={styles.link}>Сообщения</Link>
                </>
              )}

              {/* Навигация для врача */}
              {user.role === 'doctor' && (
                <>
                  <Link to="/doctor/dashboard" style={styles.link}>Дашборд</Link>
                  <Link to="/doctor/patients" style={styles.link}>Пациенты</Link>
                  <Link to="/doctor/notifications" style={styles.link}>Уведомления</Link>
                  <Link to="/messages" style={styles.link}>Сообщения</Link>
                  <Link to="/doctor/audit" style={styles.link}>Аудит</Link>
                  <Link to="/settings" style={styles.link}>Настройки</Link>
                </>
              )}

              <Link to="/about" style={styles.link}>О системе</Link>
              <span style={styles.userName}>
                {user.first_name} {user.last_name}
              </span>
              <button
                onClick={toggleTheme}
                style={styles.themeBtn}
                title={isDark ? 'Светлая тема' : 'Тёмная тема'}
              >
                {isDark ? '\u2600\uFE0F' : '\uD83C\uDF19'}
              </button>
              <button onClick={handleLogout} style={styles.logoutBtn}>
                Выйти
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Основная область контента */}
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
};

/** Inline-стили компонента */
const styles: Record<string, React.CSSProperties> = {
  nav: {
    backgroundColor: 'var(--bg-nav)',
    padding: '0 24px',
    boxShadow: 'var(--shadow-nav)',
  },
  navContent: {
    maxWidth: 1200,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 60,
  },
  logo: {
    color: '#fff',
    textDecoration: 'none',
    fontSize: 20,
    fontWeight: 700,
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  link: {
    color: 'var(--text-link)',
    textDecoration: 'none',
    fontSize: 15,
    padding: '6px 12px',
    borderRadius: 6,
    transition: 'background 0.2s',
  },
  userName: {
    color: 'var(--text-link)',
    fontSize: 14,
    marginLeft: 8,
  },
  themeBtn: {
    background: 'var(--bg-nav-btn)',
    color: '#fff',
    border: 'none',
    padding: '6px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
  },
  logoutBtn: {
    background: 'var(--bg-nav-btn)',
    color: '#fff',
    border: 'none',
    padding: '6px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
  },
  main: {
    flex: 1,
    maxWidth: 1200,
    margin: '0 auto',
    padding: '24px 16px',
    width: '100%',
  },
};

export default Layout;
